import { type RefObject, useEffect, useMemo, useRef } from "react";

import type { DiagnosticAnswer, DiagnosticContent } from "@/lib/api";

import { AUTO_CONTINUE_HINT, CLOSING } from "../copy";
import { resolveScrollContainer, scrollWithin } from "../scroll";
import type { Phase } from "../state";
import { Chips } from "./chips";
import { TypingDots } from "./typing-dots";

/** An answer that has been sent but is still waiting on `/score`. */
export interface PendingAnswer {
  chips: string[];
  text: string;
}

interface InterviewProps {
  content: DiagnosticContent;
  answers: DiagnosticAnswer[];
  /** The question currently being asked, 0 based. */
  step: number;
  phase: Phase;
  /** Indices of the chips selected on the current question. */
  selected: number[];
  /** True once the current answer has been sent. */
  locked: boolean;
  onToggleChip: (index: number) => void;
  /**
   * True while the multi-select answer is counting down to sending itself, so
   * the visitor knows they can keep picking or go now.
   */
  autoContinuing: boolean;
  /** The answer being scored right now, shown as a bubble plus a typing line. */
  pending: PendingAnswer | null;
  /**
   * True once the user has answered at least once in this session. Guards the
   * focus move so a restored session does not steal focus on mount.
   */
  takeFocus: boolean;
  reducedMotion: boolean;
  /**
   * True when the chat is a fixed-height scroll region (embedded, where the
   * frame must not grow as the conversation does): the current question is
   * pulled to the top of the region and the answered ones scroll out of view.
   * False on its own page, where the column scrolls to the newest message.
   */
  pinCurrentQuestion?: boolean;
  /**
   * The region the chat scrolls in. Only this region (or, on its own page
   * below 900px, the window) is ever scrolled: never the page around a frame.
   */
  scrollContainerRef?: RefObject<HTMLElement | null>;
}

/** Room left above a pinned question, in CSS pixels. */
const PIN_MARGIN = 24;

function trackName(content: DiagnosticContent, track: number): string {
  return content.tracks.find((item) => item.id === track)?.name ?? "";
}

/** The chat column: one AI message per question, one bubble per answer. */
export function Interview({
  content,
  answers,
  step,
  phase,
  selected,
  locked,
  onToggleChip,
  autoContinuing,
  pending,
  takeFocus,
  reducedMotion,
  pinCurrentQuestion = false,
  scrollContainerRef,
}: InterviewProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const currentRef = useRef<HTMLDivElement>(null);
  const firstChipRef = useRef<HTMLButtonElement>(null);
  const questions = content.questions;
  // The interview is over at the result and stays over once the report is
  // written: the chat below is the same in both phases.
  const finished = phase === "result" || phase === "report";
  // Every question that has been asked so far, including the one on screen.
  const asked = finished ? questions.length : Math.min(step + 1, questions.length);
  const lastReflection = answers[answers.length - 1]?.reflection ?? "";

  useEffect(() => {
    const behavior = reducedMotion ? "auto" : "smooth";
    const container = resolveScrollContainer(
      scrollContainerRef?.current ?? null,
      pinCurrentQuestion,
    );
    if (!container) {
      return;
    }
    const current = pinCurrentQuestion ? currentRef.current : null;
    if (current) {
      scrollWithin(container, current, behavior, { margin: PIN_MARGIN });
      return;
    }
    const bottom = bottomRef.current;
    if (bottom) {
      scrollWithin(container, bottom, behavior, { align: "nearest" });
    }
  }, [answers.length, step, phase, pending, reducedMotion, pinCurrentQuestion, scrollContainerRef]);

  // Sending an answer disables the chips it was picked from, which drops focus
  // onto <body> and forces a keyboard user to tab from the top of the page for
  // every question (SPEC section 7). Hand focus to the new question instead and
  // leave the scrolling above to do the scrolling.
  useEffect(() => {
    if (phase !== "interview" || !takeFocus) {
      return;
    }
    firstChipRef.current?.focus({ preventScroll: true });
  }, [step, phase, takeFocus]);

  // What a screen reader is told when the conversation moves on. Only the new
  // message goes in the live region: announcing the whole chat would re-read
  // every question that has already been answered.
  const announcement = useMemo(() => {
    if (finished) {
      return [lastReflection, CLOSING].filter(Boolean).join(" ");
    }
    const current = questions[asked - 1];
    if (!current) {
      return "";
    }
    const reflection = answers[asked - 2]?.reflection ?? "";
    return [reflection, `Question ${asked} of ${questions.length}.`, current.question]
      .filter(Boolean)
      .join(" ");
  }, [answers, asked, finished, lastReflection, questions]);

  return (
    <div className="flex flex-col gap-7">
      <p aria-atomic="true" aria-live="polite" className="sr-only">
        {announcement}
      </p>

      {questions.slice(0, asked).map((question, index) => {
        const answer = answers[index];
        const reflection = index === 0 ? "" : (answers[index - 1]?.reflection ?? "");
        const isCurrent = index === asked - 1 && phase === "interview";
        // Answered questions keep their chips on screen, locked, showing what
        // was picked. Only the question on screen is still interactive.
        const chipSelection = answer
          ? question.options.reduce<number[]>(
              (picked, option, optionIndex) =>
                (answer.chips ?? []).includes(option.label) ? [...picked, optionIndex] : picked,
              [],
            )
          : selected;

        return (
          <div
            key={question.id}
            className="flex flex-col gap-7"
            ref={isCurrent ? currentRef : undefined}
          >
            <div className="max-w-[54ch]">
              <p className="hw-mono">
                <span aria-hidden="true">
                  {index + 1}/{questions.length}
                </span>
                <span className="sr-only">
                  Question {index + 1} of {questions.length}, {trackName(content, question.track)}
                </span>
              </p>

              {reflection && <p className="hw-body text-hw-soft mt-3">{reflection}</p>}

              <p className="hw-h4 mt-3">{question.question}</p>

              {(isCurrent || answer) && (
                <Chips
                  options={question.options}
                  selected={chipSelection}
                  locked={Boolean(answer) || locked}
                  onToggle={onToggleChip}
                  firstChipRef={isCurrent ? firstChipRef : undefined}
                />
              )}

              {isCurrent && autoContinuing && <p className="hw-mono mt-3">{AUTO_CONTINUE_HINT}</p>}
            </div>

            {answer && <AnswerBubble chips={answer.chips ?? []} text={answer.text ?? ""} />}
          </div>
        );
      })}

      {pending && (
        <>
          <AnswerBubble chips={pending.chips} text={pending.text} />
          <TypingDots />
        </>
      )}

      {finished && (
        <div className="max-w-[54ch]">
          {lastReflection && <p className="hw-body text-hw-soft">{lastReflection}</p>}
          <p className="hw-h4 mt-3">{CLOSING}</p>
        </div>
      )}

      <div ref={bottomRef} aria-hidden="true" />
    </div>
  );
}

/** One sent answer, chips first, as it reads in the chat. */
function AnswerBubble({ chips, text }: { chips: string[]; text: string }) {
  return (
    <div className="flex justify-end">
      <div className="border-hw-line bg-hw-card text-hw-soft hw-body max-w-[42ch] rounded-[16px_16px_4px_16px] border px-4 py-3 whitespace-pre-line">
        {[chips.join("; "), text].filter(Boolean).join("\n")}
      </div>
    </div>
  );
}
